import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Sparkles, Calendar, ChevronRight, Users, Target } from 'lucide-react'
import { api } from '@/lib/api'
import WeeklyWizard from '@/components/WeeklyWizard'
import type { WeeklyPlan } from '@/types/models'
import { formatDate } from '@/lib/utils'

interface Props {
  onOpenPlan: (planId: string) => void
}

export default function Dashboard({ onOpenPlan }: Props) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const [wizardOpen, setWizardOpen] = useState(false)

  const { data: plansData, isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const token = await getToken()
      return api.get<{ data: WeeklyPlan[] }>('/plans', token!)
    },
  })

  const plans = (plansData as { data: WeeklyPlan[] } | undefined)?.data ?? []

  function handleWizardComplete(plan: WeeklyPlan) {
    setWizardOpen(false)
    queryClient.invalidateQueries({ queryKey: ['plans'] })
    onOpenPlan(plan.id)
  }

  return (
    <div>
      {wizardOpen && (
        <WeeklyWizard
          onComplete={handleWizardComplete}
          onCancel={() => setWizardOpen(false)}
        />
      )}

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Your Meal Plans</h1>
          <p className="text-gray-500 mt-1">Plan your week, generate your grocery list.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setWizardOpen(true)} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Week
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card p-5 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-3" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : plans.length === 0 ? (
        <div className="card p-12 text-center">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No meal plans yet</h3>
          <p className="text-gray-500 mb-6 max-w-sm mx-auto">
            Create your first weekly meal plan to get started. The wizard takes about 30 seconds.
          </p>
          <button onClick={() => setWizardOpen(true)} className="btn-primary inline-flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Create My First Plan
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <button
              key={plan.id}
              onClick={() => onOpenPlan(plan.id)}
              className="card p-5 hover:shadow-md transition-all text-left group hover:border-brand-300 border-2 border-transparent"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="font-semibold text-gray-900 group-hover:text-brand-700 transition-colors">
                    Week of {formatDate(plan.week_start_date)}
                  </p>
                  <p className="text-sm text-gray-400 mt-0.5">{plan.days_count} days planned</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    plan.status === 'complete' ? 'bg-green-100 text-green-700' :
                    plan.status === 'active' ? 'bg-brand-100 text-brand-700' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {plan.status}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {plan.default_group_size} {plan.default_group_size === 1 ? 'person' : 'people'}
                </span>
                <span className="flex items-center gap-1">
                  <Target className="w-3 h-3" />
                  {plan.goal_mode === 'calories'
                    ? `${plan.calorie_target ?? '—'} kcal`
                    : `${plan.protein_target_g ?? '—'}g protein`}
                </span>
              </div>

              {plan.diet_types?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {plan.diet_types.slice(0, 3).map(d => (
                    <span key={d} className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded-full capitalize">
                      {d.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-400">Click to open Meal Builder</span>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-brand-500 transition-colors" />
              </div>
            </button>
          ))}

          {/* Add new plan card */}
          <button
            onClick={() => setWizardOpen(true)}
            className="card p-5 border-2 border-dashed border-gray-200 hover:border-brand-400 hover:bg-brand-50/30 flex flex-col items-center justify-center gap-2 min-h-[160px] transition-all text-gray-400 hover:text-brand-600"
          >
            <Plus className="w-8 h-8" />
            <span className="text-sm font-medium">Plan Another Week</span>
          </button>
        </div>
      )}
    </div>
  )
}
